import { createContext, useEffect, useState } from "react";
//import jwt_decode from "jwt-decode";
import { eraseCookie, getCookie, setCookie } from "@utilities/cookies";
import { getUserType } from "@utilities/getUserType";
import { logoutUser } from "@utilities/navigation";
import setGADataLayer from "@utilities/setGADataLayer";
import "core-js/stable/atob";
import { jwtDecode } from "jwt-decode";
import { REFRESH_URL } from "../constants";

export type User = {
  lbsNumber: string;
  token: string;
  userData: UserInfo;
};

interface UserInfo {
  exp: number;
  iat: number;
  updated_at: number;
  aud: string;
  sub: string;
  app_metadata: {
    authorization: {
      roles: string[];
    };
  };
  email: string;
}

type UserContextValue = {
  user: User | null;
  isUserLoading: boolean;
};

export const UserContext = createContext<UserContextValue>({
  user: null,
  isUserLoading: true,
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);

  const checkIdTokenValidity = () => {
    const idToken = getCookie("id_token");
    if (idToken) {
      const decodedAccessToken: UserInfo = jwtDecode(idToken);
      const secondsTimestamp = decodedAccessToken.exp;
      const millisecondsTimestamp = secondsTimestamp * 1000;
      if (Date.now() >= millisecondsTimestamp) {
        const loc: Location = window.location;
        const redirectUrl =
          loc.protocol + "//" + loc.host + loc.pathname + loc.search;
        setCookie({
          cookieName: "logoutRedirectUrl",
          value: redirectUrl,
          expiryDays: 999999,
        });
        logoutUser("logout");
      }
    }
  };

  const checkSessionValidity = () => {
    const idToken = getCookie("id_token");
    const loginTime = getCookie("login_time");
    if (idToken) {
      const decodedIdToken = jwtDecode(idToken);
      const pswChangedAt = decodedIdToken["password_changed_at"];
      const emailChangedAt = decodedIdToken["email_changed_at"];

      if (pswChangedAt) {
        if (new Date(Number(loginTime) * 1000) < new Date(pswChangedAt)) {
          logoutUser();
        }
      }
      if (emailChangedAt) {
        if (new Date(Number(loginTime) * 1000) < new Date(emailChangedAt)) {
          logoutUser();
        }
      }
    }
  };

  useEffect(() => {
    async function getRefreshToken() {
      const response = await fetch(REFRESH_URL);
      if (response.status === 200) {
        checkSessionValidity();
      } else {
        logoutUser();
      }
    }
    if (user) {
      const tokenCheckInterval = 1 * 1000; // 1 seconds
      const refreshInterval = 420000; // 7 mins
      const intervalId = setInterval(checkIdTokenValidity, tokenCheckInterval);
      const refresh = setInterval(getRefreshToken, refreshInterval);
      return () => {
        clearInterval(intervalId);
        clearInterval(refresh);
      };
    }
  }, [user]);

  useEffect(() => {
    try {
      const token = getCookie("nf_jwt");
      const idtoken = getCookie("id_token") as string;

      if (token) {
        const decoded: UserInfo = jwtDecode(token);
        const decodedIdToken = jwtDecode(idtoken) as object;
        setUser({
          userData: decoded,
          lbsNumber:
            decodedIdToken["lbs_no"] ||
            decodedIdToken["https://www.london.edu/lbs_no"],
          token: decoded.aud,
        });

        if (!sessionStorage.getItem("loggedIn")) {
          sessionStorage.setItem("loggedIn", "true");
          setGADataLayer({
            userId:
              decodedIdToken["lbs_no"] ||
              decodedIdToken["https://www.london.edu/lbs_no"],
            loginStatus: "logged in",
            userType: getUserType(decoded.email),
            event: "login",
          });
        }
      } else {
        eraseCookie("nf_jwt");
        eraseCookie("id_token");
        setUser(null);
        sessionStorage.removeItem("loggedIn");
      }
    } catch (_) {
      eraseCookie("nf_jwt");
      eraseCookie("id_token");
      setUser(null);
      sessionStorage.removeItem("loggedIn");
    } finally {
      setIsUserLoading(false);
    }
  }, []);

  return (
    <UserContext.Provider value={{ user, isUserLoading }}>
      {children}
    </UserContext.Provider>
  );
};
